import {
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  Output,
  EventEmitter,
  ViewChild,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { CommonModule } from '@angular/common';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatTabsModule } from '@angular/material/tabs';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBar } from '@angular/material/snack-bar';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { DeleteComponent } from '../../../shared/components/delete/delete.component';
import { RestaurantService } from '../../../shared/services/restaurant/restaurant.service';
import { RolesService } from '../../../shared/services/roles/roles.service';
import { LoaderService } from '../../../shared/services/loader/loader.service';
import { LoaderComponent } from '../../../shared/components/loader/loader.component';
import { Router } from '@angular/router';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { StorageService } from '../../../shared/services/storage/storage.service';
import { QeqService } from 'src/app/shared/services/qeq/qeq.service';
import { QeqPipe } from 'src/app/shared/pipes/qeq-pipe/qeq-pippe.pipe';
import { ExcelService } from 'src/app/shared/services/excel/excel.service';
import {
  Reservation,
  ReservationRecord,
  RestaurantConfig,
  BlockedDate,
} from '../../../shared/interfaces/restaurant.interface';
import { RestaurantFilterBarComponent } from '../../../shared/components/restaurant/restaurant-filter-bar/restaurant-filter-bar.component';
import { RestaurantSummaryCardsComponent } from '../../../shared/components/restaurant/restaurant-summary-cards/restaurant-summary-cards.component';
import {
  FilterBarConfig,
  SummaryCard,
} from '../../../shared/interfaces/restaurant-shared.interface';
import { ExportExcelButtonComponent } from '../../../shared/components/export-excel-button/export-excel-button.component';

@Component({
  selector: 'app-bookings-restaurant',
  imports: [
    MatIcon,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    FormsModule,
    ReactiveFormsModule,
    MatTableModule,
    MatSortModule,
    CommonModule,
    MatDialogModule,
    MatMenuModule,
    MatPaginatorModule,
    MatTabsModule,
    MatDatepickerModule,
    MatTooltipModule,
    MatSlideToggleModule,
    MatInputModule,
    LoaderComponent,
    QeqPipe,
    RestaurantFilterBarComponent,
    RestaurantSummaryCardsComponent,
    ExportExcelButtonComponent,
  ],
  providers: [ExcelService],
  templateUrl: './bookings-restaurant.component.html',
  styleUrls: [
    './bookings-restaurant.component.css',
    '../../../shared/styles/layout.css',
  ],
})
export class BookingsRestaurantComponent implements OnDestroy {
  // ===== Contadores de “extra” para el rango filtrado =====
  private extrasAssignedPending = 0; // extras del rango en PENDING
  private extrasConsumed = 0; // extras del rango en EATEN

  // Store subscriptions for cleanup
  private subscriptions: Subscription[] = [];
  private navigationSubscription: Subscription | null = null;
  private destroy$ = new Subject<void>();

  // Flag para QeQ
  public qeqDataLoaded = false;

  // Blocked dates
  private blockedDates: BlockedDate[] = [];

  constructor(
    private restaurantService: RestaurantService,
    private rolesService: RolesService,
    private loaderService: LoaderService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private storageService: StorageService,
    private qeqService: QeqService,
    private excelService: ExcelService<Reservation>,
    private snackBar: MatSnackBar
  ) {}

  config: RestaurantConfig = {
    id: 0,
    extra_menus: 0,
    start_date: '',
    end_date: null,
  };

  // Store total extra menus for interval filter
  private totalExtraMenusForInterval: number = 0;

  // ===== Helpers de fecha y conteo =====
  private toMidnight(d: Date): Date {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
  }

  private parseISODateOnly(iso: string): Date {
    // iso tipo "YYYY-MM-DD" o "YYYY-MM-DDTHH:mm:ssZ"
    const [datePart] = iso.split('T');
    const [y, m, d] = datePart.split('-').map((n) => parseInt(n, 10));
    // Usamos fecha local a medianoche para comparar de forma coherente con created_when (local)
    return this.toMidnight(new Date(y, m - 1, d));
  }

  private countMenusOf(reservation: ReservationRecord): number {
    const guests = (reservation.guests || []).filter((g) => !g.is_deleted);
    const guestsCount = guests.length || reservation.guests_number || 0;
    return 1 + guestsCount; // 1 del empleado + invitados
  }

  // ===== Helper methods for date filtering =====
  private isWeekend(date: Date): boolean {
    const dayOfWeek = date.getDay();
    return dayOfWeek === 0 || dayOfWeek === 6; // Sunday = 0, Saturday = 6
  }

  private isBlockedDate(date: Date): boolean {
    const dateString = this.formatDateForComparison(date);
    return this.blockedDates.some(
      (blockedDate) =>
        blockedDate.date === dateString &&
        ['EVENT', 'HOLIDAY', 'NO_SERVICE'].includes(blockedDate.block_type)
    );
  }

  private formatDateForComparison(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private isValidServiceDate(date: Date): boolean {
    return !this.isWeekend(date) && !this.isBlockedDate(date);
  }

  // ===== Export =====
  exportExcel(): void {
    const filteredData = this.dataSource.filteredData ?? this.dataSource.data;
    let currentPageData = filteredData;
    if (this.paginator) {
      const startIndex = this.paginator.pageIndex * this.paginator.pageSize;
      const endIndex = startIndex + this.paginator.pageSize;
      currentPageData = filteredData.slice(startIndex, endIndex);
    }

    const exportData: any[] = [];

    // First, add all reservations
    currentPageData.forEach((item: any) => {
      const guestsCeliac = (item.guests || []).filter(
        (g: any) => g.is_celiac
      ).length;
      const guestsNormal = (item.guests || []).filter(
        (g: any) => !g.is_celiac
      ).length;

      // Get the employee name using the same logic as qeqPipe
      let employeeName = item.employee_name || '';
      if (this.qeqDataLoaded) {
        const cached = this.qeqService['getQeqFromStorage']?.();
        if (cached && cached.data) {
          const qeqEmployee = cached.data.find(
            (u: any) => u.personnelnumber === item.employee_external_id
          );
          employeeName = qeqEmployee?.name || item.employee_name || '';
        }
      }

      // Add reservation data with celiac comment
      exportData.push({
        Tipo: 'Empleado',
        Empleado: employeeName,
        'Empleado ID': item.employee_external_id || '',
        Fecha: item.date_display || item.date || '',
        'Tipo de consumo':
          item.meal_type === 'BREAKFAST' ? 'Desayuno' : 'Comida',
        Estado: this.getStatusText(item.status),
        Invitados: item.guests_number ?? (item.guests ? item.guests.length : 0),
        'Invitados Celíacos': guestsCeliac,
        'Invitados Normales': guestsNormal,
        'Invitados Observaciones': this.stripHtmlTags(
          item.guests_observation || ''
        ),
        Celíaco: item.is_celiac ? 'Sí' : 'No',
        'Comentario Celíaco': this.stripHtmlTags(item.celiac_comment || ''),
        Cobrar: item.charge ?? item.punch?.charge ? 'Sí' : 'No',
      });
    });

    // Then, add all guests as separate rows
    currentPageData.forEach((item: any) => {
      if (item.guests && item.guests.length > 0) {
        // Get the employee name for reference
        let employeeName = item.employee_name || '';
        if (this.qeqDataLoaded) {
          const cached = this.qeqService['getQeqFromStorage']?.();
          if (cached && cached.data) {
            const qeqEmployee = cached.data.find(
              (u: any) => u.personnelnumber === item.employee_external_id
            );
            employeeName = qeqEmployee?.name || item.employee_name || '';
          }
        }

        // Add each guest as a separate row
        item.guests.forEach((guest: any, index: number) => {
          if (!guest.is_deleted) {
            exportData.push({
              Tipo: 'Invitado',
              Empleado: ``,
              'Empleado ID': '',
              Fecha: item.date_display || item.date || '',
              'Tipo de consumo':
                guest.meal_type === 'BREAKFAST' ? 'Desayuno' : 'Comida',
              Estado: '',
              Invitados: '',
              'Invitados Celíacos': '',
              'Invitados Normales': '',
              'Invitados Observaciones': this.stripHtmlTags(
                guest.guests_observation || ''
              ),
              Celíaco: guest.is_celiac ? 'Sí' : 'No',
              'Comentario Celíaco': this.stripHtmlTags(
                guest.celiac_comment || ''
              ),
              Cobrar: '',
            });
          }
        });
      }
    });

    // Add menu summary at the end
    // For single-day filters (hoy, manana) or any interval (we now have total from API)
    if (
      this.selectedFilter === 'hoy' ||
      this.selectedFilter === 'manana' ||
      this.selectedFilter === 'intervalo'
    ) {
      this.addMenuSummaryToExport(exportData, currentPageData, () => {
        const fileName = this.generateFileName();
        this.excelService.exportExcel(exportData, fileName);
      });
    } else {
      const fileName = this.generateFileName();
      this.excelService.exportExcel(exportData, fileName);
    }
  }

  private generateFileName(): string {
    const today = this.toMidnight(new Date());

    switch (this.selectedFilter) {
      case 'hoy':
        return `reservas-${this.formatDateForFile(today)}`;
      case 'manana': {
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        return `reservas-${this.formatDateForFile(tomorrow)}`;
      }
      case 'intervalo':
        if (this.startDate && this.endDate) {
          const start = this.formatDateForFile(this.startDate);
          const end = this.formatDateForFile(this.endDate);
          return `reservas-${start}-${end}`;
        } else {
          return `reservas-intervalo`;
        }
      default:
        return `reservas-${new Date().toLocaleDateString('es-ES')}`;
    }
  }

  private formatDateForFile(date: Date): string {
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  }

  private addMenuSummaryToExport(
    exportData: any[],
    reservations: any[],
    callback: () => void
  ): void {
    // For interval filter, use the already loaded totalExtraMenusForInterval
    if (this.selectedFilter === 'intervalo') {
      const menuStats = this.calculateMenuStatsForInterval(
        reservations,
        this.totalExtraMenusForInterval
      );
      this.appendMenuSummaryRows(exportData, menuStats, 'Intervalo');
      callback();
      return;
    }

    // For single-day filters (hoy, manana), get config for that specific date
    const targetDate = this.getStartDateForFilter(this.selectedFilter);
    if (!targetDate) {
      callback();
      return;
    }

    const targetDateString = this.formatYYYYMMDD(targetDate);
    // console.log('Adding menu summary for date:', targetDateString);

    // Get restaurant config for the specific date to get extra menus
    this.restaurantService
      .getRestaurantConfigByDate(targetDateString)
      .subscribe({
        next: (config) => {
          const menuStats = this.calculateMenuStatsForDate(
            reservations,
            config
          );
          this.appendMenuSummaryRows(
            exportData,
            menuStats,
            this.formatDate(targetDateString + 'T00:00:00')
          );
          callback();
        },
        error: (err) => {
          console.error('Error getting restaurant config for date:', err);
          callback();
        },
      });
  }

  private calculateMenuStatsForInterval(
    reservations: any[],
    totalExtraMenus: number
  ): any {
    const stats = {
      reservedMenus: 0,
      extraMenus: totalExtraMenus,
      normalMenus: 0,
      celiacMenus: 0,
      totalGuests: 0,
      celiacGuests: 0,
      total: 0,
    };

    // Count reservations
    stats.reservedMenus = reservations.length;

    // Count guests and celiacs
    let celiacEmployees = 0;

    reservations.forEach((reservation) => {
      const guestsArr = (reservation.guests || []).filter(
        (g: any) => !g.is_deleted
      );
      const regularGuests = guestsArr.filter((g: any) => !g.is_celiac).length;
      const celiacGuestsCount = guestsArr.filter(
        (g: any) => g.is_celiac
      ).length;

      stats.totalGuests += regularGuests;
      stats.celiacGuests += celiacGuestsCount;

      if (reservation.is_celiac) {
        celiacEmployees += 1;
      }
    });

    stats.celiacMenus = celiacEmployees;
    stats.normalMenus = stats.reservedMenus - stats.celiacMenus;
    if (stats.normalMenus < 0) {
      stats.normalMenus = 0;
    }

    stats.total =
      stats.normalMenus +
      stats.celiacMenus +
      stats.totalGuests +
      stats.celiacGuests +
      stats.extraMenus;

    return stats;
  }

  private appendMenuSummaryRows(
    exportData: any[],
    menuStats: any,
    dateLabel: string
  ): void {
    // Add empty row as separator
    exportData.push({
      Tipo: '',
      Empleado: '',
      'Empleado ID': '',
      Fecha: '',
      'Tipo de consumo': '',
      Estado: '',
      Invitados: '',
      'Invitados Celíacos': '',
      'Invitados Normales': '',
      Celíaco: '',
      'Comentario Celíaco': '',
      Cobrar: '',
    });

    // Add summary header
    exportData.push({
      Tipo: 'RESUMEN DE MENÚS',
      Empleado: '',
      'Empleado ID': '',
      Fecha: dateLabel,
      'Tipo de consumo': '',
      Estado: '',
      Invitados: '',
      'Invitados Celíacos': '',
      'Invitados Normales': '',
      Celíaco: '',
      'Comentario Celíaco': '',
      Cobrar: '',
    });

    // Add menu statistics
    exportData.push({
      Tipo: 'Menús Normales',
      Empleado: menuStats.normalMenus.toString(),
      'Empleado ID': '',
      Fecha: '',
      'Tipo de consumo': '',
      Estado: '',
      Invitados: '',
      'Invitados Celíacos': '',
      'Invitados Normales': '',
      Celíaco: '',
      'Comentario Celíaco': '',
      Cobrar: '',
    });

    exportData.push({
      Tipo: 'Menús Celíacos',
      Empleado: menuStats.celiacMenus.toString(),
      'Empleado ID': '',
      Fecha: '',
      'Tipo de consumo': '',
      Estado: '',
      Invitados: '',
      'Invitados Celíacos': '',
      'Invitados Normales': '',
      Celíaco: '',
      'Comentario Celíaco': '',
      Cobrar: '',
    });

    exportData.push({
      Tipo: 'Invitados Normales',
      Empleado: menuStats.totalGuests.toString(),
      'Empleado ID': '',
      Fecha: '',
      'Tipo de consumo': '',
      Estado: '',
      Invitados: '',
      'Invitados Celíacos': '',
      'Invitados Normales': '',
      Celíaco: '',
      'Comentario Celíaco': '',
      Cobrar: '',
    });

    exportData.push({
      Tipo: 'Invitados Celíacos',
      Empleado: menuStats.celiacGuests.toString(),
      'Empleado ID': '',
      Fecha: '',
      'Tipo de consumo': '',
      Estado: '',
      Invitados: '',
      'Invitados Celíacos': '',
      'Invitados Normales': '',
      Celíaco: '',
      'Comentario Celíaco': '',
      Cobrar: '',
    });

    exportData.push({
      Tipo: 'Menús Extra',
      Empleado: menuStats.extraMenus.toString(),
      'Empleado ID': '',
      Fecha: '',
      'Tipo de consumo': '',
      Estado: '',
      Invitados: '',
      'Invitados Celíacos': '',
      'Invitados Normales': '',
      Celíaco: '',
      'Comentario Celíaco': '',
      Cobrar: '',
    });

    exportData.push({
      Tipo: 'TOTAL MENÚS',
      Empleado: menuStats.total.toString(),
      'Empleado ID': '',
      Fecha: '',
      'Tipo de consumo': '',
      Estado: '',
      Invitados: '',
      'Invitados Celíacos': '',
      'Invitados Normales': '',
      Celíaco: '',
      'Comentario Celíaco': '',
      Cobrar: '',
    });
  }

  private calculateMenuStatsForDate(reservations: any[], config: any): any {
    const stats = {
      reservedMenus: 0,
      extraMenus: 0,
      normalMenus: 0,
      celiacMenus: 0,
      totalGuests: 0,
      celiacGuests: 0,
      total: 0,
    };

    // Get extra menus from config (use new field name)
    stats.extraMenus = Number(config?.extra_menus ?? 0);

    // Count reservations
    stats.reservedMenus = reservations.length;

    // Count guests and celiacs
    let celiacEmployees = 0;

    reservations.forEach((reservation) => {
      // Count guests
      const guestsArr = (reservation.guests || []).filter(
        (g: any) => !g.is_deleted
      );
      const regularGuests = guestsArr.filter((g: any) => !g.is_celiac).length;
      const celiacGuestsCount = guestsArr.filter(
        (g: any) => g.is_celiac
      ).length;

      stats.totalGuests += regularGuests;
      stats.celiacGuests += celiacGuestsCount;

      // Count celiac employees
      if (reservation.is_celiac) {
        celiacEmployees += 1;
      }
    });

    stats.celiacMenus = celiacEmployees;

    // Calculate normal menus (reserved employees - celiac employees)
    stats.normalMenus = stats.reservedMenus - stats.celiacMenus;
    if (stats.normalMenus < 0) {
      stats.normalMenus = 0;
    }

    // Calculate total
    stats.total =
      stats.normalMenus +
      stats.celiacMenus +
      stats.totalGuests +
      stats.celiacGuests +
      stats.extraMenus;

    return stats;
  }

  private formatYYYYMMDD(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // Helper method to strip HTML tags from comments
  private stripHtmlTags(html: string): string {
    if (!html) return '';
    // Create a temporary div to parse HTML and extract text content
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    return tempDiv.textContent || tempDiv.innerText || '';
  }

  private getStatusText(status: string): string {
    switch (status) {
      case 'EATEN':
        return 'Consumido';
      case 'PENDING':
        return 'Reserva';
      default:
        return 'No consumido';
    }
  }

  // ===== Filtros =====
  searchText: string = '';
  startDate: Date | null = null;
  endDate: Date | null = null;

  filterBarConfig: FilterBarConfig = {
    hasSearch: true,
    searchPlaceholder: 'Buscar trabajador',
    hasBackButton: false,
    hasDateRange: true,
    filters: [
      { value: 'hoy', label: 'Hoy' },
      { value: 'manana', label: 'Mañana' },
      { value: 'intervalo', label: 'Intérvalo' },
    ],
    paginatorOptions: [25, 50, 100],
  };

  summaryCards: SummaryCard[] = [];

  get isLoading() {
    return this.loaderService.getLoader();
  }

  getConfig() {
    const targetDate = this.getStartDateForFilter(this.selectedFilter);

    if (this.selectedFilter === 'intervalo' && this.startDate && this.endDate) {
      this.getConfigForInterval();
    } else if (targetDate) {
      // For 'hoy' or 'manana' filters
      const dateString = this.formatYYYYMMDD(targetDate);
      this.restaurantService
        .getRestaurantConfigByDate(dateString)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (config) => {
            this.config = config;
            this.totalExtraMenusForInterval = 0; // Reset interval total
            // Update summary cards after config is loaded
            this.updateSummaryCards();
            this.cdr.detectChanges();
          },
          error: (err) => {
            console.error('Error fetching restaurant config:', err);
          },
        });
    }
  }

  getConfigForInterval() {
    if (!this.startDate || !this.endDate) {
      return;
    }

    // Get all valid service dates in the range
    const validDates = this.getValidServiceDatesInRange(
      this.startDate,
      this.endDate
    );

    if (validDates.length === 0) {
      this.totalExtraMenusForInterval = 0;
      this.updateSummaryCards();
      return;
    }

    // Group dates by year and month
    const datesBody = this.groupDatesByYearAndMonth(validDates);

    const startDateString = this.formatYYYYMMDD(this.startDate);
    const endDateString = this.formatYYYYMMDD(this.endDate);

    this.restaurantService
      .getIntervalExtraMenus(startDateString, endDateString, datesBody)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          // Use total_guests from the response for the interval
          this.totalExtraMenusForInterval = response.total_guests || 0;

          // Set a dummy config for consistency
          this.config = {
            id: 0,
            extra_menus: this.totalExtraMenusForInterval,
            start_date: startDateString,
            end_date: endDateString,
          };

          this.updateSummaryCards();
          this.cdr.detectChanges();
        },
        error: (err) => {
          console.error('Error fetching interval extra menus:', err);
          this.totalExtraMenusForInterval = 0;
        },
      });
  }

  // Helper method to get valid service dates in a range
  private getValidServiceDatesInRange(start: Date, end: Date): Date[] {
    const dates: Date[] = [];
    const current = this.toMidnight(new Date(start));
    const endDate = this.toMidnight(new Date(end));

    while (current <= endDate) {
      if (this.isValidServiceDate(current)) {
        dates.push(new Date(current));
      }
      current.setDate(current.getDate() + 1);
    }

    return dates;
  }

  // Helper method to group dates by year and month for API payload
  private groupDatesByYearAndMonth(dates: Date[]): any[] {
    const grouped: Map<number, Map<number, number[]>> = new Map();

    dates.forEach((date) => {
      const year = date.getFullYear();
      const month = date.getMonth() + 1; // 1-indexed
      const day = date.getDate();

      if (!grouped.has(year)) {
        grouped.set(year, new Map());
      }

      const yearMap = grouped.get(year)!;
      if (!yearMap.has(month)) {
        yearMap.set(month, []);
      }

      yearMap.get(month)!.push(day);
    });

    // Convert to the required format
    const result: any[] = [];
    grouped.forEach((monthsMap, year) => {
      const months: any[] = [];
      monthsMap.forEach((days, month) => {
        months.push({
          month: month,
          days: days.sort((a, b) => a - b),
        });
      });

      result.push({
        year: year,
        months: months,
      });
    });

    return result;
  }

  selectedFilter: string = 'manana';

  onFilterChange(filter: string) {
    this.selectedFilter = filter;
    // Reload config based on new filter
    this.getConfig();
    // Reload reservation data with new filter dates
    this.loadReservationData();
  }

  private getStartDateForFilter(filter: string): Date | null {
    const today = this.toMidnight(new Date());

    switch (filter) {
      case 'hoy':
        return today;
      case 'manana': {
        const t = new Date(today);
        t.setDate(t.getDate() + 1);
        return t;
      }
      case 'intervalo':
        return this.startDate ? this.toMidnight(this.startDate) : null;
      default:
        return null;
    }
  }

  private getEndDateForFilter(filter: string): Date | null {
    if (filter === 'intervalo' && this.endDate) {
      const end = this.toMidnight(this.endDate);
      end.setHours(23, 59, 59, 999);
      return end;
    }
    return null;
  }

  applyFilters() {
    const startDate = this.getStartDateForFilter(this.selectedFilter);
    const endDate = this.getEndDateForFilter(this.selectedFilter);

    const filterValue = JSON.stringify({
      dateFilter: {
        start: startDate ? startDate.getTime() : null,
        end: endDate ? endDate.getTime() : null,
      },
      searchText: this.searchText.toLowerCase(),
    });

    this.dataSource.filter = filterValue;

    // Recalcular contadores (E, extras, R) sobre lo ya cargado
    this.recomputeFromFiltered();
  }

  private applyCurrentFilterToDataSource() {
    const startDate = this.getStartDateForFilter(this.selectedFilter);
    const endDate = this.getEndDateForFilter(this.selectedFilter);
    const filterValue = JSON.stringify({
      dateFilter: {
        start: startDate ? startDate.getTime() : null,
        end: endDate ? endDate.getTime() : null,
      },
      searchText: this.searchText.toLowerCase(),
    });

    this.dataSource.filter = filterValue;
    this.recomputeFromFiltered();
  }

  onSearchChange(searchText: string) {
    this.searchText = searchText;
    this.applyFilters();
  }

  onDateRangeChange(dateRange: {
    startDate: Date | null;
    endDate: Date | null;
  }) {
    this.startDate = dateRange.startDate;
    this.endDate = dateRange.endDate;
    if (this.selectedFilter === 'intervalo' && this.startDate && this.endDate) {
      // Reload config for the new date range
      this.getConfig();
      // Reload reservation data with new date range
      this.loadReservationData();
    }
  }

  readonly dialog = inject(MatDialog);

  // 👇🏼 Mantenemos date (ISO) y añadimos date_display para la tabla
  dataSource: MatTableDataSource<
    ReservationRecord & { date_display: string; guests_number: number | null }
  > = new MatTableDataSource<
    ReservationRecord & { date_display: string; guests_number: number | null }
  >();

  private _liveAnnouncer = inject(LiveAnnouncer);

  displayedColumns: string[] = [
    'users',
    'id-users',
    'diet-date',
    'consumed',
    'status',
    'guests',
    'payed',
    'celiac',
    'acciones',
  ];

  private initialFilterApplied = false;

  @ViewChild(MatSort) sort: MatSort = new MatSort();
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(RestaurantFilterBarComponent)
  filterBarComponent!: RestaurantFilterBarComponent;

  ngAfterViewInit() {
    this.dataSource.sort = this.sort;
    if (this.filterBarComponent) {
      this.paginator = this.filterBarComponent.getPaginator();
      this.dataSource.paginator = this.paginator;
    }
  }

  announceSortChange(sortState: Sort) {
    if (sortState.direction)
      this._liveAnnouncer.announce(`Sorted ${sortState.direction}ending`);
    else this._liveAnnouncer.announce('Sorting cleared');
  }

  deleteSelected(id: number, nick: string) {
    const dialogRef = this.dialog.open(DeleteComponent, {
      width: '550px',
      data: {
        title: '¿Seguro que quieres eliminar esta reserva?',
        message: nick,
        confirmText: 'Eliminar',
        cancelText: 'Cancelar',
        handler: () => this.restaurantService.deleteRestaurant(id),
      },
    });

    const dialogSub = dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.dataSource.data = this.dataSource.data.filter(
          (user) => user.id !== id
        );
        this.restaurantService.triggerStatsRefresh();
      }
    });

    this.subscriptions.push(dialogSub);
  }

  formatDate(dateString: string): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  myroles: any = {};

  ngOnInit() {
    this.loaderService.showLoader();
    this.setupNavigationListener();
    this.loadRoles();
    this.loadBlockedDates();
    this.loadQeqDataAndThenReservations();

    const statsSub = this.restaurantService
      .getRefreshStatsObservable()
      .subscribe(() => {
        this.loadReservationData();
        this.loadBlockedDates(); // Refresh blocked dates when stats refresh
      });
    this.subscriptions.push(statsSub);

    this.getConfig();
  }

  loadQeqDataAndThenReservations() {
    const qeqSub = this.qeqService.getQeQ().subscribe({
      next: () => {
        this.qeqDataLoaded = true;
        this.loadReservationData();
        this.loaderService.hideLoader();
        this.cdr.detectChanges();
      },
      error: () => {
        this.loaderService.hideLoader();
        this.qeqDataLoaded = false;
        this.loadReservationData();
      },
    });
    this.subscriptions.push(qeqSub);
  }

  setupNavigationListener() {
    // (opcional) si necesitas reaccionar a navegación
  }

  loadRoles() {
    const permissions = this.rolesService.getPermissionsForFunctionality(
      'general',
      this.storageService
    );
    if (permissions) this.myroles = permissions;
  }

  loadBlockedDates() {
    const blockedDatesSub = this.restaurantService.getBlockedDays().subscribe({
      next: (blockedDates: BlockedDate[]) => {
        this.blockedDates = blockedDates.filter((date) => !date.is_deleted);
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('Error loading blocked dates:', error);
      },
    });
    this.subscriptions.push(blockedDatesSub);
  }

  // ===== Stats base (R sin extras) =====
  public reservationStats: {
    num_reservations: number;
    num_celiac_reservations: number;
    num_reservations_guests: number;
    num_celiac_guests: number;
    total_reservations: number; // R
    total_celiac_reservations: number;
    total_normal_reservations: number;
  } = {
    num_reservations: 0,
    num_celiac_reservations: 0,
    num_reservations_guests: 0,
    num_celiac_guests: 0,
    total_reservations: 0,
    total_celiac_reservations: 0,
    total_normal_reservations: 0,
  };

  // ===== Datos =====
  loadReservationData() {
    // Calculate start and end dates based on current filter
    const { startDate, endDate } = this.getDateRangeForCurrentFilter();

    if (!startDate || !endDate) {
      console.error('Invalid date range for filter:', this.selectedFilter);
      return;
    }

    const startDateStr = this.formatYYYYMMDD(startDate);
    const endDateStr = this.formatYYYYMMDD(endDate);

    // Show loader while fetching reservations
    this.loaderService.showLoader();

    const reservationsSub = this.restaurantService
      .getReservationsParams(startDateStr, endDateStr)
      .subscribe({
        next: (reservations: ReservationRecord[]) => {
          const activeReservations = reservations.filter((r) => !r.is_deleted);

          // ⬇️ Mantenemos date (ISO) y creamos date_display SOLO para UI
          this.dataSource.data = activeReservations.map((r) => ({
            ...r,
            date_display: this.formatDate(r.date), // << para la tabla y el filtro visual
            guests_number: r.guests_number ?? r.guests?.length ?? 0,
          }));

          this.setupFilterPredicateFunction();

          if (!this.initialFilterApplied) {
            this.applyCurrentFilterToDataSource();
            this.initialFilterApplied = true;
          } else {
            this.applyCurrentFilterToDataSource();
          }

          this.loaderService.hideLoader();
          this.cdr.detectChanges();
        },
        error: (error) => {
          console.error('Error loading reservations:', error);
          this.loaderService.hideLoader();
        },
      });

    this.subscriptions.push(reservationsSub);
  }

  // Helper to get date range for the current filter
  private getDateRangeForCurrentFilter(): {
    startDate: Date | null;
    endDate: Date | null;
  } {
    const today = this.toMidnight(new Date());

    switch (this.selectedFilter) {
      case 'hoy':
        return { startDate: today, endDate: today };
      case 'manana': {
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        return { startDate: tomorrow, endDate: tomorrow };
      }
      case 'intervalo':
        if (this.startDate && this.endDate) {
          return {
            startDate: this.toMidnight(this.startDate),
            endDate: this.toMidnight(this.endDate),
          };
        }
        // Default to today if no interval is set
        return { startDate: today, endDate: today };
      default:
        return { startDate: today, endDate: today };
    }
  }

  // ===== Filtrado tabla (usa date_display) =====
  setupFilterPredicateFunction() {
    this.dataSource.filterPredicate = (data: any, filter) => {
      const filterObj = JSON.parse(filter);
      let dateMatch = true;
      let searchMatch = true;

      if (filterObj.dateFilter.start) {
        const dateParts = data.date_display?.split('/'); // << usamos la fecha formateada
        if (dateParts?.length === 3) {
          const day = parseInt(dateParts[0], 10);
          const month = parseInt(dateParts[1], 10) - 1;
          const year = parseInt(dateParts[2], 10);

          const reservationDate = new Date(year, month, day).getTime();
          const startDate = filterObj.dateFilter.start;
          const endDate = filterObj.dateFilter.end;

          if (filterObj.dateFilter.end) {
            dateMatch =
              reservationDate >= startDate && reservationDate <= endDate;
          } else {
            const filterDate = new Date(startDate);
            const resDate = new Date(reservationDate);
            dateMatch =
              resDate.getDate() === filterDate.getDate() &&
              resDate.getMonth() === filterDate.getMonth() &&
              resDate.getFullYear() === filterDate.getFullYear();
          }
        }
      }

      if (filterObj.searchText) {
        const cached = this.qeqDataLoaded
          ? this.qeqService['getQeqFromStorage']?.()
          : null;
        const employeeName =
          cached && cached.data
            ? cached.data.find(
                (u: any) => u.personnelnumber === data.employee_external_id
              )?.name || ''
            : '';

        searchMatch = !!(
          (data.employee_name &&
            data.employee_name.toLowerCase().includes(filterObj.searchText)) ||
          (data.employee_external_id &&
            data.employee_external_id
              .toLowerCase()
              .includes(filterObj.searchText)) ||
          (employeeName &&
            employeeName.toLowerCase().includes(filterObj.searchText))
        );
      }

      return dateMatch && searchMatch;
    };
  }

  // ===== Recalcular todo lo derivado del filtro actual =====
  private recomputeFromFiltered() {
    const usingFilter = !!(
      this.dataSource.filter && this.dataSource.filter.trim() !== ''
    );
    const current = usingFilter
      ? (this.dataSource.filteredData as any[])
      : (this.dataSource.data as any[]);
    if (!current) return;

    // Recalcular extras y R sobre los datos filtrados
    this.computeExtrasForFiltered(current as ReservationRecord[]);
    this.updateStatsFromFiltered(current as ReservationRecord[]);
    this.updateSummaryCards();
  }

  // ===== E total del filtro (excluding blocked dates and weekends) =====
  private getTotalEForCurrentFilter(): number {
    // For interval filter, use the total from the API response
    if (this.selectedFilter === 'intervalo') {
      return this.totalExtraMenusForInterval;
    }

    // For 'hoy' or 'manana', use the single day config
    const perDay = Number(this.config?.extra_menus || 0);
    if (!perDay) return 0;

    // Check if it's a valid service day
    const targetDate = this.getStartDateForFilter(this.selectedFilter);
    if (!targetDate) return 0;

    return this.isValidServiceDate(targetDate) ? perDay : 0;
  }

  // ===== Count valid service days (excluding weekends and blocked dates) =====
  private getValidServiceDaysInDateRange(
    startDate: Date,
    endDate: Date
  ): number {
    if (!startDate || !endDate) return 0;

    let validDays = 0;
    const start = this.toMidnight(startDate);
    const end = this.toMidnight(endDate);

    // Iterate through each day in the range
    const currentDate = new Date(start);
    while (currentDate <= end) {
      if (this.isValidServiceDate(currentDate)) {
        validDays++;
      }
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return validDays;
  }

  // ===== Cómputo de extras (asignados pend/consumidos) basado en “misma-día” =====
  private computeExtrasForFiltered(reservations: ReservationRecord[]) {
    let extrasPending = 0;
    let extrasEaten = 0;

    reservations.forEach((r: any) => {
      // OJO: r.date aquí sigue siendo ISO original (no lo hemos tocado),
      // y r.created_when viene en ISO. Comparamos ambas por fecha (a medianoche).
      const reservationDate = this.parseISODateOnly(r.date); // fecha del servicio
      const createdDate = this.toMidnight(new Date(r['created_when'])); // fecha de creación

      const isSameDay = createdDate.getTime() === reservationDate.getTime();

      // La reserva se considera “extra” si fue creada el MISMO día de la reserva
      if (isSameDay) {
        const menus = this.countMenusOf(r);
        if (r.status === 'PENDING') extrasPending += menus;
        else if (r.status === 'EATEN') extrasEaten += menus;
      }
    });

    this.extrasAssignedPending = extrasPending;
    this.extrasConsumed = extrasEaten;
  }

  // ===== Recuento de R (sin extras) y desgloses sobre “current” =====
  private updateStatsFromFiltered(current?: ReservationRecord[]) {
    const rows =
      current ??
      (this.dataSource.filter && this.dataSource.filter.trim() !== ''
        ? (this.dataSource.filteredData as any as ReservationRecord[])
        : (this.dataSource.data as any as ReservationRecord[]));

    if (!rows) return;

    // R = reservas NO creadas el mismo día
    const regularReservations = rows.filter((r: any) => {
      const createdDate = this.toMidnight(new Date(r['created_when']));
      const reservationDate = this.parseISODateOnly(r.date);
      return createdDate.getTime() !== reservationDate.getTime();
    });

    const employeeMenus = regularReservations.length;
    const employeeCeliacMenus = regularReservations.filter(
      (r: any) => r.is_celiac
    ).length;

    const guestTotals = regularReservations.reduce(
      (acc, r: any) => {
        const guests = (r.guests || []).filter((g: any) => !g.is_deleted);
        acc.total += guests.length || r.guests_number || 0;
        acc.celiac += guests.filter((g: any) => g.is_celiac).length;
        return acc;
      },
      { total: 0, celiac: 0 }
    );

    const totalMenusR = employeeMenus + guestTotals.total;
    const totalCeliacMenus = employeeCeliacMenus + guestTotals.celiac;
    const totalNormalMenus = totalMenusR - totalCeliacMenus;

    this.reservationStats = {
      num_reservations: employeeMenus,
      num_celiac_reservations: employeeCeliacMenus,
      num_reservations_guests: guestTotals.total,
      num_celiac_guests: guestTotals.celiac,
      total_reservations: totalMenusR, // = R
      total_normal_reservations: totalNormalMenus,
      total_celiac_reservations: totalCeliacMenus,
    } as typeof this.reservationStats;
  }

  // ===== Tarjetas =====
  @Output() extraMenusChange = new EventEmitter<number>();

  updateSummaryCards() {
    const R = this.reservationStats.total_reservations; // sin extras
    const E = this.getTotalEForCurrentFilter(); // excluding weekends and blocked dates

    const extrasSinAsignar = Math.max(
      0,
      E - this.extrasAssignedPending - this.extrasConsumed
    );

    // Emitir a padre si hace falta
    this.extraMenusChange.emit(extrasSinAsignar);

    const menusTotales = R + E;

    this.summaryCards = [
      {
        title: 'MENÚS RESERVADOS',
        value: R,
        subtitle: `Empleados: ${this.reservationStats.num_reservations} | Invitados: ${this.reservationStats.num_reservations_guests}`,
      },
      {
        title: 'MENÚS',
        value: this.reservationStats.total_normal_reservations,
        subtitle: `Empleados: ${
          this.reservationStats.num_reservations -
          this.reservationStats.num_celiac_reservations
        } | Invitados: ${
          this.reservationStats.num_reservations_guests -
          this.reservationStats.num_celiac_guests
        }`,
      },
      {
        title: 'MENÚS CELÍACOS',
        value: this.reservationStats.total_celiac_reservations,
        subtitle: `Empleados: ${this.reservationStats.num_celiac_reservations} | Invitados: ${this.reservationStats.num_celiac_guests}`,
      },
      {
        title: 'MENÚS EXTRA',
        value: E,
        subtitle: `Disponibles: ${extrasSinAsignar} | Pendientes: ${this.extrasAssignedPending} | Consumidos: ${this.extrasConsumed}`,
      },
      {
        title: 'MENÚS TOTALES',
        value: menusTotales, // R + E
      },
    ];
  }

  // ===== Utilidades =====
  getDaysInDateRange(startDate: Date, endDate: Date): number {
    if (!startDate || !endDate) return 1;
    const start = this.toMidnight(startDate);
    const end = this.toMidnight(endDate);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // inclusivo
  }

  resetFilters() {
    this.searchText = '';
    this.startDate = null;
    this.endDate = null;
    this.selectedFilter = 'hoy';
    this.dataSource.data = [];
    this.loadReservationData();
  }

  ngOnDestroy() {
    this.subscriptions.forEach((s) => !s.closed && s.unsubscribe());
    this.subscriptions = [];
    if (this.navigationSubscription) {
      this.navigationSubscription.unsubscribe();
      this.navigationSubscription = null;
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  viewDetails(id: number) {
    this.router.navigate(['/home/dining/general/reservation-form', id]);
  }

  goToAddReservation() {
    const extrasDisponibles = Math.max(
      0,
      this.getTotalEForCurrentFilter() -
        this.extrasAssignedPending -
        this.extrasConsumed
    );
    if (extrasDisponibles <= 0) {
      this.snackBar.open(
        'No hay menús extra disponibles, no es posible añadir una reserva.',
        'Cerrar',
        { duration: 5000 }
      );
    } else {
      this.router.navigate(['/home/dining/general/reservation-form']);
    }
  }

  togglePaymentStatus(
    reservationId: number,
    currentStatus: boolean,
    employeeName: string
  ) {
    const idx = this.dataSource.data.findIndex(
      (r: any) => r.id === reservationId
    );
    if (idx === -1) return;

    const reservation = this.dataSource.data[idx];
    const actualCurrentStatus = reservation.punch?.charge ?? reservation.charge;
    const newStatus = !actualCurrentStatus;

    // Optimistic UI
    this.dataSource.data[idx].charge = newStatus;
    if (this.dataSource.data[idx].punch)
      this.dataSource.data[idx].punch.charge = newStatus;
    this.dataSource.data = [...this.dataSource.data];
    this.cdr.detectChanges();

    const apiSub = this.restaurantService
      .updatePaymentStatus(reservationId, newStatus)
      .subscribe({
        next: () => {
          const statusText = newStatus ? 'pagado' : 'no pagado';
          this.snackBar.open(`Reserva marcada como ${statusText}`, 'Cerrar', {
            duration: 3000,
            panelClass: ['snackbar-success'],
          });
        },
        error: () => {
          // Revertir si falla
          this.dataSource.data[idx].charge = actualCurrentStatus;
          if (this.dataSource.data[idx].punch)
            this.dataSource.data[idx].punch.charge = actualCurrentStatus;
          this.dataSource.data = [...this.dataSource.data];
          this.cdr.detectChanges();

          this.snackBar.open(
            'Error al actualizar el estado de pago',
            'Cerrar',
            { duration: 4000, panelClass: ['snackbar-error'] }
          );
        },
      });

    this.subscriptions.push(apiSub);
  }

  onExportClick() {
    this.exportExcel();
  }
}
